import React, { useCallback, useMemo } from "react";
import * as Scrivito from "scrivito";
import { ModalHeader } from "reactstrap";
import NiceModal from "@ebay/nice-modal-react";
import { useMutation } from "urql";
import * as Yup from "yup";

import {
  useCountryList,
  useProjectStatuses,
  useTranslate,
  useUserDepartments,
} from "hooks";
import { MAX_PROJECT_TITLE_LENGTH, unitOptions } from "variables/projects";
import { createNodesInput } from "utils";
import { initialNodesStructure } from "variables/initialNodesStructure";
import { useUser } from "stores/user";

import { ProjectDetailsForm } from "./ProjectDetailsForm";
import type { FormValues, StatusOption } from "./ProjectDetailsForm";
import { ModalWrapper } from "../../ModalWrapper";
import { useMandatorOptions } from "./hooks/useMandatorOptions";
import { useIsSameProj, useLanguage } from "stores/globalStore";
import type { Project } from "stores/project/project/projectStore";
import type { SelectOption } from "interfaces/shared.types";
import type { Node } from "interfaces/schemas";
import type { ResultOf } from "gql.tada";
import type { ProjectCreateMut, SameProjectCreateMut } from "api/mutations";

type CreateProjectMutationResult = ResultOf<typeof ProjectCreateMut>;
type CreateSameProjectMutationResult = ResultOf<typeof SameProjectCreateMut>;

type TreeNode = {
  folderName: string;
  nodes?: TreeNode[];
  [key: string]: unknown;
};

type Props = {
  project?: Project | null;
  mutation: Parameters<typeof useMutation>[0];
  requestParams?: Node | null;
  isUpdate?: boolean;
  resetCallback?: () => void;
};

export const ProjectDetailsModal = NiceModal.create(
  Scrivito.connect(
    ({
      project,
      mutation,
      requestParams,
      isUpdate = false,
      resetCallback = () => {},
    }: Props): React.ReactElement => {
      const { selected: preselectedDepartment } = useUserDepartments() ?? {};

      const translate = useTranslate();
      const modal = NiceModal.useModal();
      const projectStatuses = useProjectStatuses();
      const lang = useLanguage();
      const isSameProj = useIsSameProj();

      const user = useUser();
      const countryList = useCountryList({ language: lang });
      const [{ fetching, stale }, execute] = useMutation(mutation);

      const translateTree = (node: TreeNode): TreeNode => {
        const { folderName, nodes, ...rest } = node;
        return {
          ...rest,
          folderName: translate.stringOnly(folderName as string),
          nodes: nodes?.map(translateTree),
        };
      };
      const nodesResult = createNodesInput({
        data: initialNodesStructure as Node,
        callback: (node) => translateTree(node as TreeNode) as Node,
      });
      const nodesToRequest = useMemo(
        () => ("error" in nodesResult ? [] : nodesResult.nodesToRequest),
        [nodesResult],
      );

      const optionsList = useMemo(
        () =>
          projectStatuses?.map(
            (item): StatusOption => ({
              value: item?.status ?? "",
              label: translate.stringOnly(
                `PROJECT_STATUS_${item?.status?.toUpperCase() ?? ""}`,
              ),
              color: item?.color ?? "",
            }),
          ),
        [projectStatuses, translate],
      );

      const unitOptionsList: SelectOption[] = unitOptions.map(
        ({ value, label }) => ({
          value,
          label: translate(`PROJECT_UNITS_${label.toUpperCase()}`),
        }),
      );

      const countriesOptions = useMemo(
        () =>
          (countryList ?? [])
            .filter((item) => item != null)
            .map(
              ({ displayName, isoTwoLetterCode }): SelectOption => ({
                value: isoTwoLetterCode ?? "",
                label: displayName ?? "",
              }),
            ),
        [countryList],
      );

      const productData =
        requestParams &&
        "orderCode" in requestParams &&
        requestParams?.orderCode
          ? {
              seriesId: requestParams.orderCode.seriesId,
              productName: requestParams.productName,
            }
          : null;

      const mandatorOptions = useMandatorOptions(productData);

      const country = useMemo(() => {
        if (project?.country) {
          return countriesOptions?.find(
            (item) => item.value === project?.country,
          );
        }
        return user?.tenant
          ? countriesOptions?.find(
              (item) => item.value === user?.tenant?.toLowerCase(),
            )
          : countriesOptions && countriesOptions[0];
      }, [countriesOptions, project, user]);

      const initialValues = useMemo(
        () => ({
          title: project?.title,
          constructionTitle: project?.constructionTitle,
          projectNumber: project?.projectNumber,
          description: project?.description,
          status: optionsList?.find(
            (item) => item.value === (project?.status?.status || "draft"),
          ),
          country: country || { value: "de", label: "Germany" },
          mandator:
            mandatorOptions.find(
              (item) => item.value === Number(project?.mandator),
            ) || mandatorOptions[0],
          units:
            unitOptionsList.find(
              (item) => item.value === project?.unit?.unit,
            ) || unitOptionsList[0],
          departmentId: preselectedDepartment,
        }),
        [
          project,
          optionsList,
          country,
          mandatorOptions,
          unitOptionsList,
          preselectedDepartment,
        ],
      );

      const validationSchema = useMemo(
        () =>
          Yup.object({
            title: Yup.string()
              .trim(
                "The contact name cannot include leading and trailing spaces",
              )
              .min(4, "PROJECT_NAME_VALIDATION_SHORT")
              .required("PROJECT_NAME_VALIDATION_EMPTY")
              .test("max-length", function validateTitleLength(value) {
                return (
                  value.length <= MAX_PROJECT_TITLE_LENGTH ||
                  this.createError({
                    message: `"PROJECT_NAME_VALIDATION_LONG" ${value.length}`,
                    path: "title",
                  })
                );
              }),
            country: Yup.object()
              .typeError(" ")
              .nullable()
              .required(
                translate.stringOnly(
                  "PROJECT_LOCATION_SELECT_VALIDATION_EMPTY",
                ),
              ),
            units: Yup.object()
              .typeError(" ")
              .nullable()
              .required(translate.stringOnly("PROJECT_UNITS_VALIDATION_EMPTY")),
          }),
        [translate],
      );

      const handleSubmit = useCallback(
        ({ ...values }: FormValues): void => {
          if (!fetching) {
            const { mandator, ...valuesWithoutMandator } = values;

            const input = project?.id
              ? {
                  id: project?.id,
                  languageCode: lang,
                  ...(isSameProj ? valuesWithoutMandator : values),
                  ...(isSameProj ? {} : { mandator: mandator?.value }),
                  status: values.status?.value || projectStatuses?.[0]?.status,
                  country: values.country?.value,
                  units: values.units?.value,
                  departmentId: values.departmentId?.value,
                }
              : {
                  ...values,
                  status:
                    values.status?.value || projectStatuses?.[0]?.status || "",
                  country: values.country?.value,
                  units: values.units?.value,
                  languageCode: lang,
                  departmentId: values.departmentId?.value,
                  nodes: nodesToRequest,
                  ...(isSameProj ? { mandator: mandator?.value } : {}),
                };

            execute({ input }).then(({ data, error }) => {
              if (!error) {
                if (!project?.id) {
                  modal.resolve(
                    isSameProj
                      ? (data as CreateSameProjectMutationResult).addSameProject
                      : (data as CreateProjectMutationResult).addProject,
                  );
                }
                if (project?.id) {
                  modal.resolve();
                }
                modal.remove();
              }
            });
            resetCallback();
          }
        },
        [
          project,
          fetching,
          execute,
          lang,
          projectStatuses,
          nodesToRequest,
          resetCallback,
          modal,
          isSameProj,
        ],
      );

      return (
        <ModalWrapper
          isOpen={modal.visible}
          centered={true}
          size="xl"
          toggle={modal.hide}
          isLoading={fetching || stale}
        >
          <ModalHeader
            toggle={modal.hide}
            tag="div"
            className="d-flex align-items-start"
          >
            <h3 className="text-uppercase mb-1">
              {translate("PROJECT_PROPERTIES")}
            </h3>
            <h4 className="mb-2_5">
              {translate("PROJECT_MODAL_CREATE_SUB_TITLE")}
            </h4>
            {project?.id && (
              <h5 className="mb-0 font16 font-normal gray">
                Project ID: {project?.id}
              </h5>
            )}
            {project?.owner?.fullName && (
              <p className="mb-0 font16 font-normal gray">
                Project Owner: {project?.owner?.fullName}
              </p>
            )}
          </ModalHeader>
          <ProjectDetailsForm
            handleSubmit={handleSubmit}
            initialValues={initialValues}
            isUpdate={isUpdate}
            countriesOptions={countriesOptions}
            mandatorOptions={mandatorOptions}
            optionsList={optionsList}
            projectStatuses={projectStatuses}
            unitOptions={unitOptionsList}
            validationSchema={validationSchema}
            projectExists={!!project?.id}
          />
        </ModalWrapper>
      );
    },
  ),
);
